/**
 * Wiring layer between `mentionPlugin` and the presentational
 * `MentionPopup`. Owns the React UI state (open / query / caretRect /
 * selectedIndex), forwards plugin callbacks, intercepts the keymap, and
 * renders the popup with `caret-rect` placement.
 *
 * Consumers (MarkdownEditor) provide:
 *   - `getView` — returns the live ProseMirror EditorView, or null while
 *     the editor is still loading.
 *   - `mentionSuggestion` — search results sourced from a host-owned hook
 *     (NoteDetail calls `useMentionSearch`). Optional: when omitted, the
 *     hook is inert.
 *
 * Returns:
 *   - `pluginCallbacks` — stable refs to pass into `createMentionPlugin`.
 *   - `handleKeyDown` — wire into `editorViewOptionsCtx.handleKeyDown`.
 *   - `popup` — the JSX to render (already portaled when open).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { EditorView } from "prosemirror-view";

import {
  mentionPluginKey,
  selectMention,
  type MentionPluginCallbacks,
} from "./mentionPlugin";
import { MentionPopup } from "../MentionPopup";
import type { EntitySearchResult } from "../../../modules/episodes/types";

export interface MentionSuggestionConfig {
  readonly results: readonly EntitySearchResult[];
  readonly isLoading: boolean;
  readonly onQueryChange: (query: string, active: boolean) => void;
}

export interface UseMentionEditorUIArgs {
  readonly getView: () => EditorView | null;
  readonly mentionSuggestion?: MentionSuggestionConfig;
}

export interface UseMentionEditorUIReturn {
  readonly pluginCallbacks: MentionPluginCallbacks;
  readonly handleKeyDown: (view: EditorView, event: KeyboardEvent) => boolean;
  readonly popup: ReactNode;
}

const POPUP_CAP = 8;

export function useMentionEditorUI({
  getView,
  mentionSuggestion,
}: UseMentionEditorUIArgs): UseMentionEditorUIReturn {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [caretRect, setCaretRect] = useState<DOMRect | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const filteredResults = useMemo<readonly EntitySearchResult[]>(() => {
    const raw = mentionSuggestion?.results ?? [];
    return raw.filter((r): r is EntitySearchResult => r.name !== null).slice(0, POPUP_CAP);
  }, [mentionSuggestion?.results]);

  const isLoading = mentionSuggestion?.isLoading ?? false;

  // Latest values exposed via ref so plugin callbacks (created once) read fresh
  // state without re-installing the plugin on every render.
  const stateRef = useRef({
    open,
    selectedIndex,
    results: filteredResults,
    onQueryChange: mentionSuggestion?.onQueryChange,
  });
  stateRef.current = {
    open,
    selectedIndex,
    results: filteredResults,
    onQueryChange: mentionSuggestion?.onQueryChange,
  };

  const pluginCallbacks = useMemo<MentionPluginCallbacks>(
    () => ({
      onOpen: (q, rect): void => {
        setOpen(true);
        setQuery(q);
        setCaretRect(rect);
        setSelectedIndex(0);
        stateRef.current.onQueryChange?.(q, true);
      },
      onUpdate: (q, rect): void => {
        setQuery(q);
        setCaretRect(rect);
        setSelectedIndex(0);
        stateRef.current.onQueryChange?.(q, true);
      },
      onClose: (): void => {
        setOpen(false);
        setQuery("");
        stateRef.current.onQueryChange?.("", false);
      },
    }),
    [],
  );

  // Re-anchor the popup when the viewport changes while open.
  useEffect(() => {
    if (!open) return undefined;
    const recalc = (): void => {
      const view = getView();
      if (!view) return;
      const state = mentionPluginKey.getState(view.state);
      if (!state?.active) return;
      const coords = view.coordsAtPos(state.to);
      setCaretRect(coordsToRect(coords));
    };
    window.addEventListener("scroll", recalc, { capture: true, passive: true });
    window.addEventListener("resize", recalc);
    return (): void => {
      window.removeEventListener("scroll", recalc, { capture: true });
      window.removeEventListener("resize", recalc);
    };
  }, [open, getView]);

  // Click-outside close.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      const popups = document.querySelectorAll(
        "[data-placement='caret-rect']",
      );
      for (const p of Array.from(popups)) {
        if (p.contains(target)) return;
      }
      const view = getView();
      if (!view) return;
      view.dispatch(view.state.tr.setMeta(mentionPluginKey, { type: "close" }));
    };
    document.addEventListener("mousedown", onDown);
    return (): void => {
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, getView]);

  const handleKeyDown = useCallback(
    (view: EditorView, event: KeyboardEvent): boolean => {
      const snap = stateRef.current;
      if (!snap.open) return false;
      const count = snap.results.length;
      switch (event.key) {
        case "ArrowDown":
          setSelectedIndex((i) => (count > 0 ? Math.min(i + 1, count - 1) : 0));
          return true;
        case "ArrowUp":
          setSelectedIndex((i) => Math.max(0, i - 1));
          return true;
        case "Enter":
        case "Tab": {
          const item = snap.results[snap.selectedIndex];
          if (item?.name == null) return true;
          selectMention(view, {
            schemaId: item.schema_id,
            entityId: item.id,
            displayName: item.name,
          });
          return true;
        }
        case "Escape":
          view.dispatch(view.state.tr.setMeta(mentionPluginKey, { type: "close" }));
          return true;
        default:
          return false;
      }
    },
    [],
  );

  const onSelect = useCallback(
    (item: EntitySearchResult): void => {
      const view = getView();
      if (!view || item.name === null) return;
      selectMention(view, {
        schemaId: item.schema_id,
        entityId: item.id,
        displayName: item.name,
      });
    },
    [getView],
  );

  const popup: ReactNode =
    open && caretRect ? (
      <MentionPopup
        placement={{ mode: "caret-rect", rect: caretRect }}
        results={filteredResults}
        isLoading={isLoading}
        selectedIndex={selectedIndex}
        hasQuery={query.length > 0}
        activeCategory={null}
        onSelect={onSelect}
        onCategorySelect={(): void => undefined}
        onCategoryBack={(): void => undefined}
      />
    ) : null;

  return { pluginCallbacks, handleKeyDown, popup };
}

function coordsToRect(c: { left: number; top: number; right: number; bottom: number }): DOMRect {
  const width = Math.max(0, c.right - c.left);
  const height = Math.max(0, c.bottom - c.top);
  return {
    left: c.left,
    top: c.top,
    right: c.right,
    bottom: c.bottom,
    x: c.left,
    y: c.top,
    width,
    height,
    toJSON: (): Record<string, never> => ({}),
  } as DOMRect;
}
